import { ResponsiveBar } from "@nivo/bar";
import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getCompetenceProfileStats } from "../../Slices/competenceProfiles";

const sections = [
  { key: "qualifications", label: "Kwalifikacje" },
  { key: "languages", label: "Języki obce" },
  { key: "itCompetences", label: "Kompetencje IT" },
  { key: "socialCompetences", label: "Kompetencje społeczne" },
  { key: "economicActivity", label: "Działalność gospodarcza" },
];

const colors = {
  "Mój wynik": "#294e6b",
  "Średnia innych": "#6dc7d9",
};

const formatNumber = (value) =>
  Number(value).toLocaleString("pl-PL", {
    maximumFractionDigits: 1,
  });

const parseValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  return null;
};

export const ProfileSectionsStatsBar = () => {
  const dispatch = useDispatch();
  const { profileId } = useParams();

  const userId = useSelector((state) => state.auth.user?.id);
  const profileStats = useSelector(
    (state) => state.competenceProfile.profileStats
  );

  useEffect(() => {
    if (!profileId || userId == null) return;

    dispatch(getCompetenceProfileStats({ profileId, userId }));
  }, [dispatch, profileId, userId]);

  const data = sections
    .map(({ key, label }) => {
      const stats = profileStats?.[key];

      return {
        section: label,
        "Mój wynik": parseValue(stats?.user),
        "Średnia innych": parseValue(stats?.usersAverage),
      };
    })
    .filter(
      (item) =>
        item["Mój wynik"] !== null ||
        item["Średnia innych"] !== null
    )
    .reverse();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        p: { xs: 2, sm: 3 },
        borderRadius: "20px",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.94), rgba(235,244,250,0.78))",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: "1px solid rgba(41,78,107,0.09)",
        boxShadow: "0 12px 36px rgba(41,78,107,0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h2"
            sx={{
              color: "#294e6b",
              fontSize: { xs: 19, sm: 22 },
              fontWeight: 700,
              letterSpacing: "-0.4px",
            }}
          >
            Twój profil kompetencyjny
          </Typography>

          <Typography
            sx={{
              mt: 0.75,
              color: "#647789",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            Porównaj swoje wyniki ze średnią innych użytkowników.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            pt: 0.75,
          }}
        >
          {Object.entries(colors).map(([label, color]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "3px",
                  bgcolor: color,
                }}
              />

              <Typography
                sx={{
                  color: "#526577",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {data.length === 0 ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "#647789", fontSize: 14 }}>
            Dane statystyczne nie są jeszcze dostępne.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto", height: '100%' }}>
          <Box
            sx={{
              height: Math.max(280, data.length * 78 + 50),
              minWidth: 360,
              width: "100%",
            }}
          >
            <ResponsiveBar
              data={data}
              keys={["Mój wynik", "Średnia innych"]}
              indexBy="section"
              layout="horizontal"
              groupMode="grouped"
              margin={{
                top: 12,
                right: 30,
                bottom: 40,
                left: 170,
              }}
              padding={0.35}
              innerPadding={5}
              borderRadius={5}
              colors={({ id }) => colors[id]}
              valueScale={{ type: "linear" }}
              indexScale={{ type: "band", round: true }}
              valueFormat={formatNumber}
              axisTop={null}
              axisRight={null}
              axisLeft={{
                tickSize: 0,
                tickPadding: 16,
                tickRotation: 0,
              }}
              axisBottom={{
                tickSize: 0,
                tickPadding: 12,
                tickValues: 5,
                format: formatNumber,
              }}
              enableGridX
              enableGridY={false}
              gridXValues={5}
              labelSkipWidth={38}
              labelSkipHeight={14}
              labelTextColor={({ id }) =>
                id === "Mój wynik" ? "#ffffff" : "#e4e8eb"
              }
              theme={{
                fontFamily: "inherit",
                fontSize: 12,
                textColor: "#526577",
                axis: {
                  domain: {
                    line: {
                      stroke: "transparent",
                    },
                  },
                  ticks: {
                    text: {
                      fill: "#526577",
                      fontSize: 12,
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: "rgba(41,78,107,0.10)",
                    strokeDasharray: "3 5",
                  },
                },
                labels: {
                  text: {
                    fontSize: 12,
                    fontWeight: 600,
                  },
                },
              }}
              tooltip={({ id, indexValue, formattedValue, color }) => (
                <Box
                  sx={{
                    bgcolor: "#ffffff",
                    borderRadius: "12px",
                    p: "12px 16px",
                    border: "1px solid rgba(41,78,107,0.10)",
                    boxShadow: "0 8px 28px rgba(41,78,107,0.16)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#294e6b",
                      fontSize: 13,
                      fontWeight: 700,
                      mb: 0.75,
                    }}
                  >
                    {indexValue}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "3px",
                        bgcolor: color,
                      }}
                    />

                    <Typography sx={{ fontSize: 12, color: "#526577" }}>
                      {id}:{" "}
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#294e6b" }}
                      >
                        {formattedValue}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              )}
              animate
              motionConfig="gentle"
              role="img"
              ariaLabel="Porównanie wyników profilu kompetencyjnego ze średnią innych użytkowników"
              barAriaLabel={({ id, formattedValue, indexValue }) =>
                `${indexValue}, ${id}: ${formattedValue}`
              }
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};




// import { ResponsiveBar } from "@nivo/bar";
// import { useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useParams } from "react-router-dom";
// import { getCompetenceProfileStats } from "../../Slices/competenceProfiles";

// const matrix = [
//   {
//     section: "qualifications",
//     translation: "Kwalifikacje",
//   },
//   {
//     section: "languages",
//     translation: "Języki obce",
//   },
//   {
//     section: "itCompetences",
//     translation: "Kompetencje IT",
//   },
//   {
//     section: "socialCompetences",
//     translation: "Kompetencje społeczne",
//   },
//   {
//     section: "economicActivity",
//     translation: "Działalność gospodarcza",
//   },
// ];

// export const ProfileSectionsStatsBar = () => {
//   const dispatch = useDispatch();
//   let { profileId } = useParams();

//   const { user } = useSelector((state) => state.auth);

//   const profileStats = useSelector(
//     (state) => state.competenceProfile.profileStats
//   );

//   console.log("profileStats", profileStats);

//   const data = Object.entries(profileStats || {}).map(([key, data]) => {
//     return {
//       section: matrix.find((item) => item.section === key).translation,
//       "Mój wynik": data.user,
//       "Mój wynikColor": "#55E098",
//       "Wynik innych": data.usersAverage,
//       "Wynik innychColor": "#404040",
//     };
//   });

//   useEffect(() => {
//     dispatch(getCompetenceProfileStats({ profileId, userId: user.id }));
//   }, [profileId, user.id, dispatch]);

//   return (
//     <div style={{ height: "500px", width: "100%" }}>
//       <ResponsiveBar
//         data={data.reverse()}
//         keys={["Mój wynik", "Wynik innych"]}
//         indexBy="section"
//         margin={{ top: 50, right: 40, bottom: 80, left: 160 }}
//         padding={0.3}
//         groupMode="grouped"
//         layout="horizontal"
//         valueScale={{ type: "linear" }}
//         indexScale={{ type: "band", round: true }}
//         colors={(bar) => bar.data[`${bar.id}Color`]}
//         fill={[
//           {
//             match: {
//               id: "Mój wynik",
//             },
//             id: "dots",
//           },
//           {
//             match: {
//               id: "Wynik innychColor",
//             },
//             id: "lines",
//           },
//         ]}
//         borderColor={{
//           from: "color",
//           modifiers: [["darker", 1.6]],
//         }}
//         axisTop={null}
//         axisRight={null}
//         labelSkipWidth={12}
//         labelSkipHeight={12}
//         labelTextColor={{
//           from: "color",
//           modifiers: [["darker", 1.6]],
//         }}
//         legends={[
//           {
//             dataFrom: "keys",
//             anchor: "bottom-right",
//             direction: "column",
//             justify: false,
//             translateX: -160,
//             translateY: 80,
//             itemsSpacing: 2,
//             itemWidth: 100,
//             itemHeight: 20,
//             itemDirection: "left-to-right",
//             itemOpacity: 0.85,
//             symbolSize: 20,
//             effects: [
//               {
//                 on: "hover",
//                 style: {
//                   itemOpacity: 1,
//                 },
//               },
//             ],
//           },
//         ]}
//         role="application"
//         ariaLabel="Wykres sekcji"
//         barAriaLabel={(e) =>
//           e.id + ": " + e.formattedValue + " in section: " + e.indexValue
//         }
//       />
//     </div>
//   );
// };
